import React, { createContext, Reducer, useReducer } from 'react';
import { isPopulated } from '../../../util/isPopulated';
import { values } from '../../../util/values';
import { isSupportedSystem, LocalSystems, localSystems } from '../../localSystems';
import { Dictionary } from '../../../util/types';
import { getSessionItem, storeSessionItem } from '../../storage/SessionStorage';

export type EnabledLocalSystems = Dictionary<boolean> & Partial<{
	[System in LocalSystems]: boolean;
}>

export type LocalSystemFilterState = {
	enabledLocalSystems: EnabledLocalSystems;
}

export type Actions = SetLocalSystemsFilter;

export type SetLocalSystemsFilter = {
	type: 'setLocalSystemsFilter';
	enabledLocalSystems: EnabledLocalSystems;
}

const key = 'localSystemFilter';
const localSystemFilter = new URLSearchParams(window.location.search).get(key) || getSessionItem(key) || '';
storeSessionItem(key, localSystemFilter);

const enabledLocalSystems: EnabledLocalSystems = localSystemFilter.split(',').reduce((systems, system) => {
	system = system.toLowerCase();

	if (!isSupportedSystem(system)) return systems;

	systems[system] = true;

	return systems;
}, {} as EnabledLocalSystems);

// Enable all systems if none are specified.
if (!isPopulated(enabledLocalSystems)) {
	for (const { id } of values(localSystems)) {
		enabledLocalSystems[id] = true;
	}
}

const Dispatch = createContext<React.Dispatch<Actions>>(() => {});
const State = createContext<LocalSystemFilterState>({
	enabledLocalSystems: {}
});

const reducer: Reducer<LocalSystemFilterState, Actions> = (state, action) => {
	switch (action.type) {
		case 'setLocalSystemsFilter':
			return { enabledLocalSystems: action.enabledLocalSystems }
		default:
			throw new Error('invalid dispatch parameters in local system reducer');
	}
}

function Provider({ children }: { children: JSX.Element[] | JSX.Element }) {
	const [state, dispatch] = useReducer(reducer, {
		enabledLocalSystems
	});

	// The state and the dispatch function should be provided separately so that components that are interested
	// only in dispatching events don't need to use the state and thus re-render when the state changes
	// See here - https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
	return (
		<State.Provider value={state}>
			<Dispatch.Provider value={dispatch}>{children}</Dispatch.Provider>
		</State.Provider>
	)
}

export const LocalSystemFilter = {
	State,
	Dispatch,
	Provider
};